import {
    Action,
    Module,
    Mutation,
    MutationAction,
    VuexModule,
    getModule,
} from 'vuex-module-decorators';
import { encodeUriDeviceId } from '~/helpers';
import { DeviceData } from '~/interfaces/device.interface';
import { PlantDevice } from '~/interfaces/eliot.interface';
import { HomeData } from '~/interfaces/home.interface';
import { SupportDataParams } from '~/interfaces/route.interface';
import { SearchQuery, SearchState } from '~/interfaces/search.interface';
import { UserData } from '~/interfaces/user.interface';
import { Plant } from '~/models/plant.model';
import router from '~/router';
import apiService from '~/services/api.service';
import { ConfigModule } from '~/store/modules/config';
import { DevicesModule } from '~/store/modules/devices';
import { HomesModule } from '~/store/modules/homes';
import { OrderModule } from '~/store/modules/order';
import { RoomsModule } from '~/store/modules/rooms';
import { UserModule } from '~/store/modules/user';
import {
    deviceIdDbNoSemiColumnRe,
    deviceIdDbRe,
    gatewaySomfy,
    guidRe,
    orderIdRe,
    sapIdRe,
    serialRe,
    userIdDbRe,
    userMailRe,
} from '~common-helpers';
import store from '~common-store';
import { LoadingModule } from '~common-store/modules/loading';
import { NotificationsModule } from '~common-store/modules/notifications';
import { EliotModule } from './eliot';
import { LayoutModule } from './layout';

@Module({ dynamic: true, store, name: 'search', namespaced: true })
class Search extends VuexModule implements SearchState {
    currentSearch: SearchQuery = {
        searchText: '',
        paramType: '',
    };

    selectedPrefix = '';

    get buildQueryParams(): { [key: string]: string } {
        const query: { [key: string]: string } = {};
        query[this.currentSearch.paramType] = decodeURIComponent(this.currentSearch.searchText);
        return query;
    }

    get buildAndGoToRoute() {
        return (
            res: { user: UserData, homes?: HomeData[], devices: DeviceData[] | PlantDevice[], plants?: Plant[] },
            searchText: string,
            isEliot: boolean,
            paramType?: string,
            fullParams?: SupportDataParams,
        ): void => {
            let route = '';
            // if full parameters are given, construct the route with them
            if (fullParams) {
                route = isEliot ? this.buildEliotRouteFromFullParams(fullParams) : this.buildNetatmoRouteFromFullParams(fullParams);
                // use router.replace and not router.push for browser history:
                // we don't lose history when for example searching for a user
                // then searching for another user, and then going back with browser history
                // to the previous user. If we do router.push there, browser history '=>' is overwritten
                // and no entry exists anymore
                router.replace(route);
                // otherwise, construct the route from the response
            } else {
                route = isEliot
                    ? this.buildEliotRouteFromApiResponse(res as { user: UserData, plants: Plant[], devices: PlantDevice[] }, searchText)
                    : this.buildNetatmoRouteFromApiResponse(res as { user: UserData, homes: HomeData[], devices: DeviceData[] }, searchText, paramType);
                router.push(route);
            }
        };
    }

    get buildNetatmoRouteFromFullParams() {
        return (fullParams: SupportDataParams): string => {
            const { userIdentifier, homeId } = fullParams;
            let { gatewayId, moduleId } = fullParams;

            moduleId = encodeUriDeviceId(moduleId!);
            gatewayId = encodeUriDeviceId(gatewayId!);

            return (userIdentifier ? `/${ConfigModule.supportAppNameBase}/user/${encodeURIComponent(userIdentifier)}` : `/${ConfigModule.supportAppNameBase}`)
                + (homeId ? `/home/${homeId}` : '')
                + (gatewayId ? `/gateway/${gatewayId}` : '')
                + (moduleId ? `/module/${moduleId}` : '');
        };
    }

    get buildEliotRouteFromFullParams() {
        return (fullParams: SupportDataParams): string => {
            const { userIdentifier, plantId, eliotGatewayId } = fullParams;
            return (userIdentifier ? `/${ConfigModule.supportAppNameBase}/eliotuser/${encodeURIComponent(userIdentifier)}` : `/${ConfigModule.supportAppNameBase}`)
                + (plantId ? `/plant/${plantId}` : '')
                + (eliotGatewayId ? `/eliotgateway/${eliotGatewayId}` : '');
        };
    }

    get buildNetatmoRouteFromApiResponse() {
        return (
            res: { user: UserData, homes: HomeData[], devices: DeviceData[] },
            searchText: string,
            paramType?: string,
        ): string => {
            let route = '/' + ConfigModule.supportAppNameBase;
            const { user, homes, devices } = res;
            if (user) {
                route += `/user/${this.isUserId(searchText) ? user.user_id : encodeURIComponent(searchText)}`;
            } else if (homes || devices) {
                let deviceFound;
                if (homes) {
                    // if we have homes and no user, it should be the only home in the "homes" array
                    const homeFound = homes[0];
                    route += `/home/${homeFound.id}`;
                    if (paramType !== 'home_id' && homeFound.devices) {
                        deviceFound = this.findDevice(homeFound.devices, searchText);
                        if (!deviceFound) {
                            NotificationsModule.pushNotification({
                                title: 'Device not found in home',
                                text: 'product commissioning error + device is not in the home',
                                type: 'error',
                                duration: 20000,
                            });
                        }
                    }
                } else {
                    deviceFound = this.findDevice(devices, searchText);
                }
                if (deviceFound) {
                    // determine if the device found is a gateway or a module by looking at the bridge field
                    route += deviceFound.bridge
                        ? `/gateway/${deviceFound.bridge}/module/${deviceFound.id}`
                        : `/gateway/${deviceFound.id}`;

                    route = route.replaceAll('somfy://', encodeURIComponent('somfy://'));
                }
            }
            return route;
        };
    }

    get buildEliotRouteFromApiResponse() {
        return (
            res: { user: UserData, plants: Plant[], devices: PlantDevice[] },
            searchText: string,
        ): string => {
            let route = '/' + ConfigModule.supportAppNameBase;
            const { user, devices } = res;
            if (user) {
                route += `/eliotuser/${this.isUserId(searchText) ? user.user_id : encodeURIComponent(searchText)}`;
            } else if (devices) {
                // there is only one device in the devices array returned by the api
                route += `/eliotgateway/${devices[0].macAddress || devices[0].id}`;
            }
            return route;
        };
    }

    get isOrderIdentifier() {
        return (string: string): boolean => orderIdRe.test(string) || sapIdRe.test(string);
    }

    get isUserId() {
        return (string: string): boolean => userIdDbRe.test(string);
    }

    get isUserIdentifier() {
        return (string: string): boolean => this.isUserId(string) || this.isValidEmail(string);
    }

    get findDevice() {
        return (devices: DeviceData[], id: string): DeviceData | undefined => devices.find((module: DeviceData) => module.id === id);
    }

    get isValidEmail() {
        return (email: string): boolean => userMailRe.test(String(email).toLowerCase());
    }

    get prefixFromText() {
        return (text: string): string | null => {
            const prefixRe = /^(vaillant:\/\/|sdbg:\/\/|muller:\/\/|velux:\/\/)/;
            const match = text.match(prefixRe);
            return match && match[0];
        };
    }

    @Action
    setCurrentSearch(searchQuery: SearchQuery): void {
        this.context.commit('SET_CURRENT_SEARCH', searchQuery);
    }

    @MutationAction
    async setSelectedPrefix(selectedPrefix: string): Promise<{ selectedPrefix: string }> {
        window.localStorage.setItem('searchPrefix', selectedPrefix);
        return { selectedPrefix };
    }

    @Action
    async getSupportData(searchQuery: SearchQuery): Promise<{ user: any, homes: any, devices: any } | void> {
        try {
            LoadingModule.showLoading(true);
            this.setCurrentSearch(searchQuery);
            const response = await apiService.getsupportdata(this.buildQueryParams);
            const { user, homes, devices } = response.data.body;
            LoadingModule.showLoading(false);
            return { user, homes, devices };
        } catch (error) {
            return;
        }
    }

    @Action
    async getEliotData(searchQuery: SearchQuery): Promise<{ user: any, plants: any, devices: any } | void> {
        try {
            LoadingModule.showLoading(true);
            this.setCurrentSearch(searchQuery);
            const response = await apiService.geteliotdata(this.buildQueryParams);
            const { user, plants, devices } = response.data.body;
            LoadingModule.showLoading(false);
            return { user, plants, devices };
        } catch (error) {
            return;
        }
    }

    @Action
    async search(
        { searchText, paramType, fullParams, prefix }:
        { searchText: string, paramType?: string, fullParams?: SupportDataParams, prefix?: string },
    ): Promise<void> {
        searchText = searchText.toLowerCase().trim();
        let res;
        try {
            const isEliot = prefix === 'legrand://';
            if (isEliot) {
                res = await this.searchEliotDatabase({ searchText, paramType });
            } else {
                ({ prefix, searchText, paramType } = setPrefixSearchTextParamType(searchText, prefix, paramType));
                res = await this.searchNetatmoDatabase({ searchText, paramType });
            }

            // if nothing was found, exit function
            if (!res) {
                return;
            }

            // reset tree search after doing a new search
            LayoutModule.setTreeFilter('');

            // reset modules to start with clean data
            this.resetModules();

            // set modules for eliot or netatmo data properly
            if (isEliot) {
                this.setEliotModules(res);
            } else {
                this.setNetatmoModules(res);
                UserModule.setPrefix(prefix || '');
            }
            // build route from the search result, as well as the parameters of the route we land on from if we come from one
            this.buildAndGoToRoute(res, searchText, isEliot, paramType, fullParams);

        } catch (error) {
            return;
        }
    }

    @Action
    async searchNetatmoDatabase(
        { searchText, paramType }:
        { searchText: string, paramType: string },
    ): Promise<{ user: any, homes: any, devices: any } | void> {
        // add try/catch block to avoid pushing a route if the search has failed
        try {
            if (paramType !== 'home_id' && !this.isUserIdentifier(searchText) && !isDeviceIdentifier(searchText)) {
                if (this.isOrderIdentifier(searchText)) {
                    searchText = searchText.toUpperCase().trim();
                    // Search by Order number
                    const order = await OrderModule.searchOrder(searchText);
                    if (order) {
                        router.push(`/${ConfigModule.supportAppNameBase}/order/${order.orderNumber}`);
                    }
                    return;
                } else {
                    // Invalid search input
                    NotificationsModule.pushNotification({
                        title: 'Invalid user mail / user id / device id / order id',
                        type: 'error',
                    });
                    return;
                }
            }
            return await this.getSupportData({ searchText, paramType });
        } catch (error) {
            return;
        }
    }

    @Action
    async searchEliotDatabase(
        { searchText, paramType }:
        { searchText: string, paramType?: string },
    ): Promise<{ user: any, plants: any, devices: any } | void> {
        // add try/catch block to avoid pushing a route if the search has failed
        try {
            if (this.isValidEmail(searchText)) {
                // Search by User email or User Id
                paramType = 'user_mail';
                searchText = encodeURIComponent(searchText);
            } else if (isEliotSearchParam(searchText)) {
                paramType = 'search';
            } else {
                // Invalid search input
                NotificationsModule.pushNotification({
                    title: 'Invalid user mail / mac address / device id / serial number',
                    type: 'error',
                });
                return;
            }
            return await this.getEliotData({ searchText, paramType });
        } catch (error) {
            return;
        }
    }

    @Action
    resetModules(): void {
        UserModule.reset();
        DevicesModule.reset();
        RoomsModule.reset();
        HomesModule.reset();
        EliotModule.reset();
    }

    @Action
    setNetatmoModules({ user, homes, devices }: {
        user?: UserData,
        homes?: HomeData[],
        devices?: DeviceData[]
    }): void {
        if (user) {
            UserModule.setUser(user);
        }
        if (homes) {
            HomesModule.setHomes(homes);
        }
        if (devices) {
            DevicesModule.setDevices({ devices });
        }
    }

    @Action
    setEliotModules({ user, plants, devices }: {
        user?: UserData,
        plants?: Plant[],
        devices?: PlantDevice[]
    }): void {
        if (user) {
            UserModule.setUser({ ...user, env: 'eliot' });
        }
        if (devices) {
            // geteliotdata will return an array of devices but there should only be one so we add the first one
            EliotModule.addDevice({ device: devices[0] });
        }
        if (plants) {
            EliotModule.setPlants(plants);
        }
    }

    @Mutation
    SET_CURRENT_SEARCH(searchQuery: SearchQuery): void {
        this.currentSearch = searchQuery;
    }
}

function addSemiColumnsToMacAddress(noSemiColumnMacAddress: string): string {
    return (noSemiColumnMacAddress.match(/(.{2})/g) as string[]).join(':');
}

function convertSerialToMacAddress(serialNumber: string): string {
    const map = new Map([
        ['g', '70ee50'],
        ['h', '020000'],
        ['i', '030000'],
        ['j', '040000'],
        ['k', '050000'],
        ['l', '060000'],
        ['m', '070000'],
        ['n', '080000'],
        ['o', '090000'],
        ['p', '0a0000'],
        ['q', '0b0000'],
        ['r', '0c0000'],
        ['s', '0d0000'],
        ['t', '0e0000'],
        ['u', '0f0000'],
        ['mg', '000474'],
    ]);
    // slice(0, -6) to remove the last 6 characters of the string,
    // to get the particle we want to map ('g', 'h', ..., 'mg')
    // then slice(-6) to get the last 6 characters of the string
    const mappedSerialNumber = map.get(serialNumber.slice(0, -6)) + serialNumber.slice(-6);
    return addSemiColumnsToMacAddress(mappedSerialNumber);
}

function reformatDeviceId(deviceId: string): { searchText: string, paramType: string } {
    // if device identifier is an id and has no semicolumn, add them
    // example 70ee50033008 to 70:ee:50:03:30:08
    if (deviceIdDbNoSemiColumnRe.test(deviceId)) {
        deviceId = addSemiColumnsToMacAddress(deviceId);
    } else if (serialRe.test(deviceId)) {
        deviceId = convertSerialToMacAddress(deviceId);
    }
    return { searchText: deviceId, paramType: 'device_id' };
}

function setPrefixSearchTextParamType(searchText: string, prefix?: string, paramType?: string): { searchText: string, prefix?: string, paramType: string } {
    // check that param type is not known to be home id and that the search text matches that of a user identifier (id or mail)
    // (we need to check paramType !== 'home_id', because it would match the regex of a user id)
    if (paramType !== 'home_id' && SearchModule.isUserIdentifier(searchText)) {
        // Search by User email or User Id
        paramType = SearchModule.isUserId(searchText) ? 'user_id' : 'user_mail';
        if (paramType === 'user_mail') {
            // add prefix (legrand://, sdbg://, ...) to user mail if there is one
            if (prefix) {
                searchText = prefix + searchText;
            } else {
                /*
                    * If we don't have a prefix in parameter but we have a prefix in the mail,
                    * we need to add it to the store so that we can match the mail with its prefix.
                    */
                prefix = SearchModule.prefixFromText(searchText) || '';
            }
        }
    } else if (isDeviceIdentifier(searchText)) {
        ({ searchText, paramType } = reformatDeviceId(searchText));
    }

    return { searchText, prefix, paramType: paramType || '' };
}

function isDeviceIdentifier(string: string): boolean {
    return deviceIdDbRe.test(string) || serialRe.test(string) || deviceIdDbNoSemiColumnRe.test(string) || gatewaySomfy.test(string);
}

function isEliotSearchParam(string: string): boolean {
    return deviceIdDbRe.test(string) || guidRe.test(string);
}

export const SearchModule = getModule(Search);
